import { useCallback, useEffect, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";

/** Mirrors attendance.service.ts's fallback when no shift is assigned. */
export const DEFAULT_SHIFT_HOURS = 9;

// Today's attendance is one fact about the employee, but it is shown in two
// places at once (the top-bar clock and the attendance page) which mount
// independently and would otherwise each hold their own stale copy -- check in
// on the page and the clock's Check Out would stay disabled until a reload.
// Every mounted hook subscribes here and refetches when any of them changes it.
const listeners = new Set<() => void>();

/** Tells every mounted useTodayAttendance to refetch. Call after any check-in
 *  or check-out made outside this hook (e.g. AttendancePage's own handler). */
export function notifyAttendanceChanged(): void {
  for (const fn of listeners) fn();
}

/** Subscribe to the same signal without taking on the hook's own fetching --
 *  for views that hold their own attendance data (e.g. the dashboard widget's
 *  calendar) and only need to know when to reload. Returns an unsubscribe. */
export function subscribeAttendanceChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export interface TodayAttendanceRecord {
  id: string;
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  isToday?: boolean;
  /** Yesterday's record can still be checked out (the night shift); older ones cannot. */
  isYesterday?: boolean;
  /** Closed by the missed check-out job: finished, with no check-out time until corrected. */
  autoClosedAt?: string | null;
  shiftHours: number | null;
}

export interface AttendanceActions {
  /** Checked in today or yesterday (night shift) and not checked out: still at work. */
  isOnTheClock: boolean;
  /** The latest check-in was never checked out and can no longer be: needs a correction. "YYYY-MM-DD", or null. */
  missedCheckoutDate: string | null;
  canCheckIn: boolean;
  canCheckOut: boolean;
}

/**
 * What the employee may do next, from their latest record -- the rules the API applies
 * (attendance.service.ts checkIn / checkOut), so the buttons never offer what it refuses.
 * Shared by the top-bar clock and the attendance page.
 *
 *  - Only today's or yesterday's open record can be checked out. Anything older, or a
 *    record the missed check-out job closed, is a missed check-out: it never blocks a new
 *    check-in and is fixed through a correction.
 *  - Yesterday's open record blocks today's check-in only until its shift is over.
 */
export function attendanceActions(record: TodayAttendanceRecord | null, hasShift: boolean): AttendanceActions {
  const unclosed = !!record?.checkIn && !record.checkOut;
  const recent = !!record?.isToday || !!record?.isYesterday;
  const isOnTheClock = unclosed && recent && !record?.autoClosedAt;
  const missedCheckoutDate = unclosed && !isOnTheClock ? record!.date.slice(0, 10) : null;

  const shiftLimitHours = record?.shiftHours ?? DEFAULT_SHIFT_HOURS;
  const elapsedHours = isOnTheClock ? (Date.now() - new Date(record!.checkIn!).getTime()) / 3_600_000 : 0;
  // Yesterday's still-open record is superseded once the shift it belonged to has fully
  // elapsed -- otherwise an overnight shift would be closed out by a fresh check-in.
  const openExpired = isOnTheClock && !record?.isToday && elapsedHours >= shiftLimitHours;
  // Today's record closed as a missed check-out is finished too: one check-in a day.
  const todayCompleted = !!record?.isToday && !!record.checkIn && (!!record.checkOut || !!record.autoClosedAt);

  return {
    isOnTheClock,
    missedCheckoutDate,
    // Check-OUT stays available without a shift on purpose: anyone already mid-day
    // when the shift rule started applying must still be able to close their record.
    canCheckIn: hasShift && (isOnTheClock ? openExpired : !todayCompleted),
    canCheckOut: isOnTheClock,
  };
}

export interface TodayAttendance extends AttendanceActions {
  record: TodayAttendanceRecord | null;
  loading: boolean;
  /** False once we know no shift is assigned: check-in is refused without one. */
  hasShift: boolean;
  /** Both reject on failure, so an AsyncButton shows the error state. */
  checkIn: () => Promise<void>;
  checkOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Today's attendance record and the rules for what the employee may do next.
 *
 * The eligibility rules live here rather than in a component so the top-bar
 * clock and the attendance page can never disagree about whether someone is
 * allowed to check in; AttendancePage applies the same attendanceActions to its
 * own copy of the record.
 */
export function useTodayAttendance(): TodayAttendance {
  const toast = useToast();
  const [record, setRecord] = useState<TodayAttendanceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  // Starts true so the button does not flicker disabled while this loads; the API
  // is the authority either way and refuses a check-in with no shift.
  const [hasShift, setHasShift] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [today, shift] = await Promise.all([
        api.get<{ data: TodayAttendanceRecord[] }>("/attendance/me?limit=1"),
        api.get<{ hasShift: boolean }>("/me/shift-status"),
      ]);
      setRecord(today.data.data[0] ?? null);
      setHasShift(shift.data.hasShift);
    } catch (err) {
      logError("useTodayAttendance.refresh", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const onChange = () => { void refresh(); };
    listeners.add(onChange);
    return () => { listeners.delete(onChange); };
  }, [refresh]);

  const actions = attendanceActions(record, hasShift);

  const act = useCallback(async (type: "checkin" | "checkout") => {
    try {
      let payload: Record<string, unknown> = type === "checkin" ? { source: "WEB" } : {};

      if (type === "checkin" && navigator.geolocation) {
        // Best-effort: a refused or slow fix must not block the check-in.
        const pos = await new Promise<GeolocationPosition | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { timeout: 5000 });
        });
        if (pos) payload = { ...payload, geoLat: pos.coords.latitude, geoLong: pos.coords.longitude };
      }

      const { data } = await api.post<TodayAttendanceRecord>(`/attendance/${type}`, payload);
      setRecord(data);
      toast.show(type === "checkin" ? "Checked in successfully" : "Checked out successfully", "success");
      await refresh();
      notifyAttendanceChanged();   // keep the other mounted view in step
    } catch (e: unknown) {
      toast.show(errMsg(e) ?? "Something went wrong", "error");
      throw e;   // AsyncButton must see the rejection, or it shows a false tick
    }
  }, [refresh, toast]);

  const checkIn  = useCallback(() => act("checkin"),  [act]);
  const checkOut = useCallback(() => act("checkout"), [act]);

  return { record, loading, ...actions, hasShift, checkIn, checkOut, refresh };
}
